import { Link } from 'react-router-dom';
import type { Project, ProjectStatus } from '@builder/shared';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import {
  MissingFieldBadge,
  detectMissingFields,
} from '@/components/ui/MissingFieldBadge';
import { useT } from '@/hooks/useT';
import { formatDate } from '@/lib/format';

/**
 * Wave geo · 2026-05-21 — Tiny inline map-pin glyph rendered before the
 * project address/city. We don't depend on lucide-react here to stay
 * tree-shake-friendly (this card is used in lazy-loaded list pages).
 */
function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

const STATUS_TONES: Record<ProjectStatus, 'muted' | 'brand' | 'warn' | 'ok' | 'neutral'> = {
  planning: 'muted',
  in_progress: 'brand',
  on_hold: 'warn',
  completed: 'ok',
  archived: 'neutral',
};

interface ProjectCardProps {
  project: Pick<
    Project,
    | 'id'
    | 'name'
    | 'address'
    | 'clientName'
    | 'status'
    | 'progressPercent'
    | 'estimatedEndDate'
    | 'coverPhotoUrl'
  > & {
    /**
     * Wave geo · 2026-05-21 — opcionais. Quando o backend já geocodou
     * o endereço, preferimos `"city, state"` ao address cru (mais
     * legível na listagem). Ambos `?` pra suportar projects fetched
     * pré-migration 0250.
     */
    city?: string | null;
    state?: string | null;
    /**
     * W126.1 · 2026-05-26 — tipo de obra opcional pra detectar fallback
     * "A definir" do wizard finalize. Quando null, MissingFieldBadge
     * mostra "Falta tipo".
     */
    tipoObra?: string | null;
  };
  href?: string;
  /**
   * Wave 379BP · #93 owner portal v2 — when > 0, the card surfaces a
   * small attention dot in the top-right of the cover photo (or, when no
   * photo, next to the title). Hidden by default → existing internal
   * project list pages don't need to pass this prop.
   *
   * Deliberately a minimal visual: a dot + count, not a verbose label.
   * Owners scan ~10 cards on the portfolio in 1 second; we want them to
   * pick up "this one needs me" peripherally.
   */
  alertCount?: number;
}

/**
 * Interaction: the whole card is the link target. Hover lifts shadow and
 * nudges the cover photo 2% up — understated but it makes the list feel
 * responsive. Focus ring uses brand-400 at low opacity so keyboard nav is
 * obvious without being ugly.
 */
export function ProjectCard({ project, href, alertCount = 0 }: ProjectCardProps) {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const target = href ?? `/projects/${project.id}`;
  const showAlert = alertCount > 0;
  // W126.1 · 2026-05-26 — detecta fallback "A definir" persistido pelo
  // wizard finalize quando PM pula address/clientName. A badge é o
  // único sinal visual de "essa obra está incompleta" no card list.
  const missingFields = detectMissingFields({
    address: project.address,
    clientName: project.clientName,
    tipoObra: project.tipoObra,
  });
  return (
    <Link
      to={target}
      className="group block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
    >
      <Card className="overflow-hidden transition-shadow duration-240 ease-out-soft group-hover:shadow-pop dark:group-hover:shadow-pop-dark">
        {project.coverPhotoUrl && (
          <div className="relative h-32 w-full overflow-hidden bg-surface-3">
            <img
              src={project.coverPhotoUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-500 ease-out-soft group-hover:scale-[1.03]"
              loading="lazy"
            />
            {showAlert && (
              <span
                className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-warn px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm tabular-nums"
                title={isEn
                  ? `${alertCount} ${alertCount === 1 ? 'item needs your attention' : 'items need your attention'}`
                  : `${alertCount} ${alertCount === 1 ? 'pendência aguardando sua atenção' : 'pendências aguardando sua atenção'}`}
                aria-label={isEn
                  ? `${alertCount} ${alertCount === 1 ? 'item needs your attention' : 'items need your attention'}`
                  : `${alertCount} ${alertCount === 1 ? 'pendência aguardando sua atenção' : 'pendências aguardando sua atenção'}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
                {alertCount}
              </span>
            )}
          </div>
        )}
        <CardBody className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold tracking-tight text-ink">
                {project.name}
              </h3>
              {/* W126.1 · 2026-05-26 — badge "Falta endereço/cliente/tipo"
                  logo abaixo do nome quando o wizard finalize persistiu
                  fallback "A definir". Deep link pra editar. */}
              {missingFields.length > 0 && (
                <div className="mt-1">
                  <MissingFieldBadge
                    fields={missingFields}
                    projectId={project.id}
                    data-testid="project-card-missing-fields"
                  />
                </div>
              )}
              {/* Wave geo · 2026-05-21 — pin glyph + endereço resumido.
                  Quando o backend geocodou, mostra "Cidade, UF" (mais
                  legível na grade). Senão, cai pro address completo.
                  W126.1 · 2026-05-26 — quando o endereço é "A definir"
                  (fallback do wizard), suprime esta linha; a badge já
                  comunica o estado de campo faltando. */}
              {!missingFields.includes('address') && (
                <p className="flex items-center gap-1 truncate text-sm text-ink-muted">
                  <MapPinIcon className="shrink-0 text-ink-subtle" />
                  <span className="truncate">
                    {project.city
                      ? `${project.city}${project.state ? `, ${project.state}` : ''}`
                      : project.address}
                  </span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {showAlert && !project.coverPhotoUrl && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-warn px-2 py-0.5 text-[11px] font-semibold text-white tabular-nums"
                  title={isEn
                    ? `${alertCount} ${alertCount === 1 ? 'item needs attention' : 'items need attention'}`
                    : `${alertCount} ${alertCount === 1 ? 'item requer atenção' : 'itens requerem atenção'}`}
                  aria-label={isEn
                    ? `${alertCount} ${alertCount === 1 ? 'item needs attention' : 'items need attention'}`
                    : `${alertCount} ${alertCount === 1 ? 'item requer atenção' : 'itens requerem atenção'}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
                  {alertCount}
                </span>
              )}
              <Badge tone={STATUS_TONES[project.status]}>{t.projects.status[project.status]}</Badge>
            </div>
          </div>
          <div className="space-y-1.5">
            <ProgressBar value={project.progressPercent} />
            <div className="flex items-center justify-between text-xs text-ink-subtle tabular-nums">
              <span>
                {project.progressPercent}% · {t.projects.progress.toLowerCase()}
              </span>
              {project.estimatedEndDate && (
                <span>
                  {t.projects.dueDate}: {formatDate(project.estimatedEndDate)}
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
